<template>
  <div class="favorite-coins mx-10">
    <div class="dropdown mexc-dropdown">
      <div class="dropdown-header mexc-dropdown-header flex items-center justify-between">
        <div class="header-controls flex items-center">
          <div class="select-form mr-2" @change="handleChange">
            <select v-model="periods" class="select-custom font-bold bg-white">
              <option value="">Select time</option>
              <option value="1m">1m</option>
              <option value="3m">3m</option>
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="2h">2h</option>
              <option value="4h">4h</option>
              <option value="6h">6h</option>
              <option value="12h">12h</option>
              <option value="1d">1d</option>
            </select>
          </div>
          <div class="metrics-dropdown relative mr-2" @keydown.stop>
            <button type="button"
              class="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-bold hover:bg-gray-100"
              @click="isMetricsOpen = !isMetricsOpen" :aria-expanded="isMetricsOpen">
              {{ metricsLabel }}
              <svg viewBox="0 0 20 20" class="w-4 h-4">
                <path d="M5 7l5 5 5-5" fill="currentColor" />
              </svg>
            </button>
            <div v-show="isMetricsOpen" ref="metricsMenu"
              class="metrics-menu-container absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg"
              @mouseleave="closeOnHover && (isMetricsOpen = false)">
              <div class="metrics-items-scroll">
                <label v-for="opt in metricOptions" :key="opt.value"
                  class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer select-none">
                  <input type="checkbox" class="accent-indigo-600" :value="opt.value" v-model="selectedMetrics"
                    @change="emitMetrics" />
                  <span class="text-sm text-gray-800">{{ opt.label }}</span>
                </label>
              </div>

              <div class="metrics-buttons-footer flex justify-end gap-2 px-2 py-2 border-t border-gray-200">
                <button class="text-xs text-gray-500 hover:text-gray-700 font-semibold" @click="selectAll">Select
                  all</button>
                <button class="text-xs text-gray-500 hover:text-gray-700 font-semibold" @click="clearAll">Unselect
                  all</button>
              </div>
            </div>
          </div>
          <button @click="handleRefresh"
            class="text-gray-500 bg-white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-2 focus:ring-gray-100 font-bold rounded-md text-sm px-5 py-2.5 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700 mr-2">
            Refresh
          </button>
          <!-- Nút toggle thu gọn/mở rộng list coin -->
          <button @click="toggleDropdown" class="toggle-dropdown-btn ml-1" :title="showDropdown ? 'Thu gọn danh sách' : 'Mở rộng danh sách'">
            <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor" :class="{ 'rotated': showDropdown }">
              <path
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </button>
        </div>
      </div>
      <div class="dropdown-content" :class="{ 'dropdown-hidden': !showDropdown }">
        <div class="tabs font-bold">
          <button :class="['tab', { 'active': activeTab === 'all' }]" @click="activeTab = 'all'">
            All Coins
          </button>
        </div>
        <div class="search-container font-bold">
          <input type="text" v-model="searchQuery" @input="debouncedSearch" placeholder="Search by coin name..." />
        </div>
        <div class="coins-list-container">
          <div class="coins-list font-bold">
            <div v-for="coin in displayedCoins" :key="coin.id" class="coin-item"
              :class="{ 'selected': selectedCoin === coin.coinName }">
              <router-link
                class="coin-name flex-1"
                :to="{ name: 'MexcCoin', params: { coinName: coin.coinName } }"
                @click="onCoinClick(coin.coinName)">
                {{ coin.coinName }}
              </router-link>
              <span class="new-icon">
                <NewIcon v-if="coin.isNew" />
              </span>
              <span class="star-icon" @click="toggleFavorite(coin)">
                <StarIcon1 v-if="coin.favoriteFlag === '0'" />
                <StarIcon2 v-else />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StarIcon1 from "@/assets/svg/StarIcon1.vue";
import { API_URL } from "@/config";
import debounce from "lodash/debounce";
import StarIcon2 from "@/assets/svg/StarIcon2.vue";
import NewIcon from "@/assets/svg/NewIcon.vue";

export default {
  components: {
    StarIcon1,
    StarIcon2,
    NewIcon
  },
  data() {
    return {
      activeTab: 'all',
      periods: "5m",
      coins: [],
      searchQuery: "",
      filteredCoins: [],
      loading: false,
      selectedMetrics: [],
      initialMetrics: [],
      metricOptions: [
        { value: 'openInterest', label: 'Open interest' },
        { value: 'longShort', label: 'Long/Short' },
        { value: 'inflow30d', label: '5d Trading Flow' },
        { value: 'inflow24h', label: 'Trading Flow' },
        { value: 'fundingRate', label: 'Funding rate' },
      ],
      isMetricsOpen: false,
      closeOnHover: true,
      selectedCoin: null,
      showDropdown: true,
    };
  },
  async created() {
    await this.fetchCoins();
    // Lưu trạng thái metrics ban đầu
    this.initialMetrics = [...this.selectedMetrics];
    // Đồng bộ selectedCoin từ URL hiện tại
    this.selectedCoin = this.$route.params.coinName || null;
    if (this.selectedCoin) {
      this.showDropdown = false;
    }
  },
  watch: {
    '$route.params.coinName': {
      immediate: false,
      handler(val) {
        this.selectedCoin = val || null;
        // Tự động thu gọn list khi có coin được chọn qua URL
        if (val) {
          this.showDropdown = false;
        }
      }
    }
  },
  computed: {
    displayedCoins() {
      // Sort coins: favorites first, then others
      return [...this.filteredCoins].sort((a, b) => {
        // Favorite coins (favoriteFlag === "1") come first
        if (a.favoriteFlag === "1" && b.favoriteFlag !== "1") return -1;
        if (a.favoriteFlag !== "1" && b.favoriteFlag === "1") return 1;
        return 0; // Keep original order for coins in the same group
      });
    },
    metricsLabel() {
      if (!this.selectedMetrics.length) return 'Select metrics';
      if (this.selectedMetrics.length === this.metricOptions.length) return 'All metrics';
      const names = this.metricOptions
        .filter(o => this.selectedMetrics.includes(o.value))
        .map(o => o.label);
      return names.length <= 2 ? names.join(', ') : `${names.length} selected`;
    },
  },
  methods: {
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    onCoinClick(coinName) {
      this.selectedCoin = coinName;
      // Tự động thu gọn list sau khi click
      this.showDropdown = false;
    },
    async handleFetchCoins() {
      this.$emit("handle-fetchCoins");
    },
    async handleChange() {
      this.$emit("period-selected", this.periods);
    },
    async handleRefresh() {
      const metricsChanged = JSON.stringify(this.selectedMetrics.sort()) !== JSON.stringify(this.initialMetrics.sort());
      if (metricsChanged && !this.selectedMetrics.length) {
        this.$toast.open({
          message: 'Vui lòng chọn ít nhất một metrics!',
          type: 'error',
          position: 'top-right',
        });
        return;
      }
      this.$emit("refresh-clicked", this.periods);
    },
    async fetchCoins() {
      try {
        const response = await fetch(`${API_URL}/mexc/get-coins`);
        const data = await response.json();
        this.coins = data.map(coin => ({
          ...coin,
          favoriteFlag: coin.favoriteFlag || "0",
        }));
        this.filteredCoins = this.coins;
        // Phát sự kiện báo cho cha biết đã load xong dữ liệu
        this.$emit("coins-loaded", data);
      } catch (error) {
        console.error("Error fetching coins:", error);
      }
    },

    debouncedSearch: debounce(function () {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        this.filteredCoins = this.coins;
      } else {
        const exactMatch = [];
        const startsWithMatch = [];
        const containsMatch = [];

        for (const coin of this.coins) {
          const name = coin.coinName.toLowerCase();
          if (name === query) {
            exactMatch.push(coin);
          } else if (name.startsWith(query)) {
            startsWithMatch.push(coin);
          } else if (name.includes(query)) {
            containsMatch.push(coin);
          }
        }
        this.filteredCoins = [...exactMatch, ...startsWithMatch, ...containsMatch];
      }
    }, 300),

    async toggleFavorite(coin) {
      if (this.loading) return;
      this.loading = true;

      try {
        const newFavoriteFlag = coin.favoriteFlag === "1" ? "0" : "1";
        coin.favoriteFlag = newFavoriteFlag;
        const requestBody = [{
          coinName: coin.coinName,
          favoriteFlag: coin.favoriteFlag,
        }];
        const response = await fetch(`${API_URL}/mexc/update-favorite`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error("Failed to update favorite status");
        }
        const data = await response.json();
        this.coins = data.map(coin => ({
          ...coin,
          favoriteFlag: coin.favoriteFlag || "0",
        }));
        this.filteredCoins = this.coins;
        // Đồng bộ dữ liệu mới lên cha
        this.$emit("coins-loaded", data);
        this.$toast.open({
          message: `${coin.coinName} ${newFavoriteFlag === "1" ? "đã được thêm vào" : "đã xóa khỏi"} favorites!`,
          type: "success",
          position: "top-right",
        });
      } catch (error) {
        console.error("Error toggling favorite:", error);
        this.$toast.open({
          message: "Lỗi cập nhật trạng thái favorites!",
          type: "error",
          position: "top-right",
        });
      } finally {
        this.loading = false;
      }
    },
    emitMetrics() {
      this.$emit('metrics-changed', this.selectedMetrics);
    },
    selectAll() {
      this.selectedMetrics = this.metricOptions.map(o => o.value);
      this.emitMetrics();
    },
    clearAll() {
      this.selectedMetrics = [];
      this.emitMetrics();
    },

    handleClickOutside(e) {
      if (!this.isMetricsOpen) return;
      const menu = this.$refs.metricsMenu;
      if (menu && !menu.contains(e.target)) {
        const btn = e.target.closest('.metrics-dropdown');
        if (!btn || !btn.contains(this.$el)) {
          this.isMetricsOpen = false;
        }
        if (!this.$el.contains(e.target)) this.isMetricsOpen = false;
      } else if (!this.$el.contains(e.target)) {
        this.isMetricsOpen = false;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  },
};
</script>
